import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { ChevronRight, ChevronDown, Plus, Pencil, Trash2, Save, X, Book, Brain, Tag, MessageCircle, FolderOpen } from 'lucide-react'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PageHeader } from '@/components/ui/page-header'
import { useGuidedUpload } from '@/contexts/guided-upload'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCategoryManager, useCategoryOptions } from '@/hooks/use-categories'
import { useAllDocuments } from '@/hooks/use-documents'
import { useDirectories, type DirectoryNode } from '@/hooks/use-directories'
import { URLs } from '@/components/knowledge/URLs'
import { URLUploadForm } from '@/components/knowledge/URLUploadForm'
import { Documents } from '@/components/knowledge/Documents'
import { WhatTheAgentKnows } from '@/components/knowledge/WhatTheAgentKnows'
import { DocumentUploadForm } from '@/components/knowledge/DocumentUploadForm'
import { UploadGate } from '@/components/knowledge/UploadGate'
import { UploadErrorModal } from '@/components/knowledge/UploadErrorModal'
import { TagsManager } from '@/components/knowledge/TagsManager'
import { RagTester } from '@/components/knowledge/RagTester'
import { ContentManager } from '@/components/knowledge/ContentManager'

// Define types directly to avoid import issues
interface DocumentUploadData {
  title: string
  description?: string
  category?: string
  subcategory?: string
  directory?: string | null
  file?: File
  document_type: 'pdf' | 'txt' | 'csv' | 'doc'
}

interface URLUploadData {
  title: string
  url: string
  description?: string
  category?: string
  subcategory?: string
  directory?: string | null
  document_type: 'url'
  isPublic?: boolean
  auto_embed?: boolean
}

// Define Category interface locally to avoid module resolution issues
interface Category {
  id: string
  name: string
  itemCount: number
  subcategories: Category[]
}

// Define URL interface locally to avoid module resolution issues
interface URL {
  id: string
  title: string
  url: string
  description?: string
  category?: string
  subcategory?: string
  status: 'pending' | 'processing' | 'embedded' | 'failed'
  isPublic?: boolean
  createdAt: string
  updatedAt: string
}

interface CategoryItemProps {
  category: Category
  onToggle: (id: string) => void
  onEdit: (id: string, name: string) => void
  onDelete: (id: string) => void
  depth?: number
  onAddSubcategory: (parentId: string) => void
  isExpanded: boolean
}

function CategoryItem({ category, onToggle, onEdit, onDelete, depth = 0, onAddSubcategory, isExpanded }: CategoryItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editedName, setEditedName] = useState(category.name)

  const handleStartEditing = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(true)
    setEditedName(category.name)
  }

  const handleSaveEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (editedName.trim() !== "") {
      onEdit(category.id, editedName.trim())
      setIsEditing(false)
    }
  }

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(false)
    setEditedName(category.name)
  }

  return (
    <div>
      <div
        className="flex cursor-pointer items-center justify-between rounded-md p-2 hover:bg-muted/50"
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
        onClick={() => onToggle(category.id)}
      >
        {isEditing ? (
          <div className="flex flex-grow flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon-xs" className="invisible">
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Input
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              className="h-8 min-w-0 flex-1 py-1"
              autoFocus
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation()
                if (e.key === "Enter") handleSaveEdit(e as unknown as React.MouseEvent)
                if (e.key === "Escape") handleCancelEdit(e as unknown as React.MouseEvent)
              }}
            />
            <Badge variant="secondary">{category.itemCount}</Badge>
            <Button variant="ghost" size="sm" onClick={handleSaveEdit}>
              <Save className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <>
            <div className="flex min-w-0 items-center gap-2">
              <Button variant="ghost" size="icon-xs" className="shrink-0" disabled={category.subcategories.length === 0}>
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
              <span className="min-w-0 truncate" title={category.name}>{category.name}</span>
              <Badge variant="secondary" className="shrink-0">{category.itemCount}</Badge>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0"
                onClick={(e) => {
                  e.stopPropagation()
                  onAddSubcategory(category.id)
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" className="shrink-0" onClick={handleStartEditing}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0"
                disabled={category.itemCount > 0}
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete(category.id)
                }}
                title={category.itemCount > 0 ? "No se puede eliminar porque tiene documentos asociados" : "Eliminar categoría"}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </div>
      {isExpanded && category.subcategories.length > 0 && (
        <div>
          {category.subcategories.map((subcategory) => (
            <CategoryItem
              key={subcategory.id}
              category={subcategory}
              onToggle={onToggle}
              onEdit={onEdit}
              onDelete={onDelete}
              depth={depth + 1}
              onAddSubcategory={onAddSubcategory}
              isExpanded={isExpanded}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function CategoriesTabSkeleton() {
  return (
    <div className="space-y-2">
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={`category-skeleton-${index}`} className="flex items-center justify-between rounded-md border p-2.5">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-6 rounded-md" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-5 w-8 rounded-full" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8 rounded-md" />
              <Skeleton className="h-8 w-8 rounded-md" />
              <Skeleton className="h-7 w-7 rounded-md" />
              <Skeleton className="h-7 w-7 rounded-md" />
            </div>
          </div>
        ))}
      </div>
      <Skeleton className="mt-4 h-10 w-full rounded-md" />
    </div>
  )
}

interface CategoriesPanelProps {
  loading: boolean
  error: string | null
  categories: Category[]
  expandedIds: Set<string>
  isAddingCategory: boolean
  newCategoryName: string
  onToggle: (id: string) => void
  onEdit: (id: string, name: string) => void
  onDelete: (id: string) => void
  onAddSubcategory: (parentId: string) => void
  onStartAddingCategory: () => void
  onCancelAddingCategory: () => void
  onNewCategoryNameChange: (value: string) => void
  onAddCategory: () => void | Promise<void>
  retryLabel: string
  addCategoryLabel: string
  categoryNamePlaceholder: string
  saveLabel: string
  cancelLabel: string
}

function CategoriesPanel({
  loading,
  error,
  categories,
  expandedIds,
  isAddingCategory,
  newCategoryName,
  onToggle,
  onEdit,
  onDelete,
  onAddSubcategory,
  onStartAddingCategory,
  onCancelAddingCategory,
  onNewCategoryNameChange,
  onAddCategory,
  retryLabel,
  addCategoryLabel,
  categoryNamePlaceholder,
  saveLabel,
  cancelLabel,
}: CategoriesPanelProps) {
  if (loading) {
    return <CategoriesTabSkeleton />
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-destructive">{error}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => window.location.reload()}
          >
            {retryLabel}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-1">
        {categories.map((category) => (
          <CategoryItem
            key={category.id}
            category={category}
            onToggle={onToggle}
            onEdit={onEdit}
            onDelete={onDelete}
            onAddSubcategory={onAddSubcategory}
            isExpanded={expandedIds.has(category.id)}
          />
        ))}
      </div>

      <Button className="mt-4 w-full" onClick={onStartAddingCategory}>
        <Plus className="mr-2 h-4 w-4" /> {addCategoryLabel}
      </Button>

      {isAddingCategory && (
        <div className="mt-2 flex items-center gap-2">
          <Input
            value={newCategoryName}
            onChange={(e) => onNewCategoryNameChange(e.target.value)}
            placeholder={categoryNamePlaceholder}
            className="flex-1"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") void onAddCategory()
              if (e.key === "Escape") {
                onCancelAddingCategory()
              }
            }}
          />
          <Button onClick={() => void onAddCategory()}>
            <Save className="mr-2 h-4 w-4" />
            {saveLabel}
          </Button>
          <Button variant="outline" onClick={onCancelAddingCategory}>
            {cancelLabel}
          </Button>
        </div>
      )}
    </>
  )
}

export function KnowledgePage() {
  const { t } = useTranslation()
  type ContentManagerItem = NonNullable<React.ComponentProps<typeof ContentManager>["items"]>[number]
  type ContentManagerDocumentUpdate = Parameters<NonNullable<React.ComponentProps<typeof ContentManager>["onUpdateDocument"]>>[1]
  type ContentManagerUrlUpdate = Parameters<NonNullable<React.ComponentProps<typeof ContentManager>["onUpdateURL"]>>[1]
  type ContentManagerMovePayload = Parameters<NonNullable<React.ComponentProps<typeof ContentManager>["onMoveItems"]>>[0]
  
  const [activeTab, setActiveTab] = useState("content")
  const [ragMessages, setRagMessages] = useState<Array<{
    id: string
    content: string
    role: "user" | "assistant"
    timestamp: Date
  }>>([])
  const [ragMessagesHydrated, setRagMessagesHydrated] = useState(false)
  const RAG_SESSION_KEY = 'rk.knowledge.ragMessages.v1'

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const raw = window.sessionStorage.getItem(RAG_SESSION_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw) as Array<{
        id: string
        content: string
        role: "user" | "assistant"
        timestamp: string | Date
      }>
      if (!Array.isArray(parsed)) return
      setRagMessages(
        parsed
          .filter((item) => item && typeof item === 'object')
          .map((item) => {
            const role: 'user' | 'assistant' = item.role === 'assistant' ? 'assistant' : 'user'
            return {
              id: String(item.id),
              content: String(item.content ?? ''),
              role,
              timestamp: new Date(item.timestamp),
            }
          })
          .filter((item) => Number.isFinite(item.timestamp.getTime()))
      )
    } catch {
      // Ignore sessionStorage parse errors.
    } finally {
      setRagMessagesHydrated(true)
    }
  }, [])

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    if (!ragMessagesHydrated) return
    try {
      window.sessionStorage.setItem(
        RAG_SESSION_KEY,
        JSON.stringify(
          ragMessages.map((msg) => ({
            ...msg,
            timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp,
          }))
        )
      )
    } catch {
      // Ignore sessionStorage write errors.
    }
  }, [ragMessages, ragMessagesHydrated])
  // sidebar removed — stats shown as hover badge in Content tab
  const tabsContainerRef = React.useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = React.useState(false)
  const [canScrollRight, setCanScrollRight] = React.useState(false)

  // Category options hook - must be defined before useAllDocuments
  const {
    categoryOptions: formCategories
  } = useCategoryOptions()

  // API hooks - using the exact same pattern as the original frontend
  const {
    documents,
    urls,
    loading: documentsLoading,
    uploadLoading: documentsUploadLoading,
    error: documentsError,
    uploadDocument,
    uploadURL,
    deleteDocument,
    deleteURL: deleteURLFromHook,
    updateDocument,
    updateURL: updateURLFromHook,
    refetch: refetchDocuments
  } = useAllDocuments(formCategories)

  const {
    directories,
    createDirectory,
    updateDirectory,
    deleteDirectory,
  } = useDirectories()

  // Extraer URLs existentes para validación
  const existingUrls = urls.map(url => url.url)

  const {
    categories: managerCategories,
    loading: categoriesLoading,
    error: categoriesError,
    onAddCategory: addCategory,
    onDeleteCategory: deleteCategory,
    onDeleteSubcategory: deleteSubcategory,
    onUpdateCategory: updateCategory,
    onUpdateSubcategory: updateSubcategory
  } = useCategoryManager()

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [newCategoryName, setNewCategoryName] = useState("")
  const [isAddingCategory, setIsAddingCategory] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState<string | null>(null)
  const [showSubcategoryDialog, setShowSubcategoryDialog] = useState(false)
  const [newSubcategoryName, setNewSubcategoryName] = useState("")
  const [currentParentId, setCurrentParentId] = useState<string | null>(null)
  const [showURLUpload, setShowURLUpload] = useState(false)
  const [showDocumentUpload, setShowDocumentUpload] = useState(false)
  // **Subir Documento abre el selector, no el formulario** (3.1, R1). El
  // formulario sigue existiendo y sigue siendo el camino de quien ya se sabe
  // los campos (D2): se llega a el con el enlace de debajo del boton (3.3).
  const askForFile = useGuidedUpload()
  
  // Estado para el modal de error de carga
  const [showUploadErrorModal, setShowUploadErrorModal] = useState(false)
  const [uploadErrorData, setUploadErrorData] = useState<{
    filename: string
    stage: 'uploading' | 'processing' | 'embedding' | 'completed' | 'error'
    progress: number
    error?: string
    processingResult?: Record<string, unknown>
  } | null>(null)

  const tabs = [
    {
      id: "content",
      name: t('knowledge.tabs.content'),
      icon: <FolderOpen className="h-4 w-4" />,
    },
    {
      id: "tags",
      name: t('knowledge.sections.tags'),
      icon: <Tag className="h-4 w-4" />,
    },
    {
      id: "categories",
      name: t('knowledge.sections.categories'),
      icon: <Book className="h-4 w-4" />,
    },
    {
      id: "lo-que-sabe",
      name: t('knowledge.tabs.lo_que_sabe'),
      icon: <Brain className="h-4 w-4" />,
    },
    {
      id: "chat",
      name: t('knowledge.tabs.chat'),
      icon: <MessageCircle className="h-4 w-4" />,
    },
  ]

  React.useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab("content")
    }
  }, [activeTab, tabs])

  // Hook to handle scroll detection for tabs
  React.useEffect(() => {
    const container = tabsContainerRef.current
    if (!container) return

    const checkScroll = () => {
      const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth)
      setCanScrollLeft(container.scrollLeft > 0)
      setCanScrollRight(container.scrollLeft < maxScrollLeft - 1)
    }

    const resizeObserver = new ResizeObserver(checkScroll)
    resizeObserver.observe(container)
    container.addEventListener('scroll', checkScroll)
    window.addEventListener('resize', checkScroll)
    checkScroll()

    return () => {
      resizeObserver.disconnect()
      container.removeEventListener('scroll', checkScroll)
      window.removeEventListener('resize', checkScroll)
    }
  }, [])

  const toggleCategory = (id: string) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleAddCategory = async () => {
    if (newCategoryName.trim()) {
      try {
        await addCategory(null, newCategoryName.trim())
        setNewCategoryName("")
        setIsAddingCategory(false)
      } catch (error) {
        console.error('Error creating category:', error)
      }
    }
  }

  const handleEditCategory = async (id: string, name: string) => {
    try {
      // Check if it's a subcategory by looking in all subcategories
      let isSubcategory = false
      let parentCategoryId = null
      
      for (const category of managerCategories) {
        const subcategory = category.subcategories.find(sub => sub.id === id)
        if (subcategory) {
          isSubcategory = true
          parentCategoryId = category.id
          break
        }
      }
      
      if (isSubcategory) {
        // For subcategories, we need to send the category ID as well
        await updateSubcategory(id, { 
          name, 
          category: parseInt(parentCategoryId!) 
        })
      } else {
      await updateCategory(id, { name })
      }
    } catch (error) {
      console.error('Error updating category:', error)
    }
  }

  const handleDeleteCategory = (id: string) => {
    setShowDeleteDialog(id)
  }

  const confirmDeleteCategory = async (id: string) => {
    try {
      
      // This needs to check if it is a subcategory
      let isSub = false;
      for (const cat of managerCategories) {
          if (cat.subcategories.find(s => s.id === id)) {
              isSub = true;
              break;
          }
      }
      
      if (isSub) {
          // Use the specific subcategory delete function
          await deleteSubcategory(id);
      } else {
          await deleteCategory(id);
      }
      
      setShowDeleteDialog(null)
    } catch (error) {
      // El error se maneja en el hook useCategories y se muestra en la interfaz
      console.error('❌ Error deleting category:', error)
      setShowDeleteDialog(null)
    }
  }
  
  const findCategoryName = (id: string): string => {
    const findName = (categories: Category[]): string => {
      for (const category of categories) {
        if (category.id === id) {
          return category.name
        }
        if (category.subcategories.length > 0) {
          const name = findName(category.subcategories)
          if (name) return name
        }
      }
      return ""
    }

    return findName(managerCategories)
  }

  const handleShowAddSubcategoryDialog = (parentId: string) => {
    setCurrentParentId(parentId)
    setNewSubcategoryName("")
    setShowSubcategoryDialog(true)
  }

  const handleAddSubcategory = async () => {
    if (newSubcategoryName.trim() && currentParentId) {
      try {
        await addCategory(currentParentId!, newSubcategoryName.trim())
        setShowSubcategoryDialog(false)
        setCurrentParentId(null)
      } catch (error) {
        console.error('Error creating subcategory:', error)
      }
    }
  }

  // URL management functions
  const deleteURL = async (id: string) => {
    try {
      await deleteURLFromHook(id)
    } catch (error) {
      console.error('Error deleting URL:', error)
      throw error
    }
  }

  const updateURL = async (id: string, data: Record<string, unknown>) => {
    try {
      // Send data directly - the hook will handle the transformation to API format
      await updateURLFromHook(id, data as Partial<URL>)
    } catch (error) {
      console.error('Error updating URL:', error)
      throw error
    }
  }

  const parseContentItemId = (contentId: string, prefix: 'doc-' | 'url-' | 'dir-') => {
    if (!contentId.startsWith(prefix)) {
      throw new Error(`Invalid content item id: ${contentId}`)
    }
    const rawId = contentId.slice(prefix.length)
    if (!rawId) {
      throw new Error(`Missing entity id for content item: ${contentId}`)
    }
    return rawId
  }

  const handleContentDocumentUpdate = async (
    contentId: string,
    data: ContentManagerDocumentUpdate,
  ) => {
    const documentId = parseContentItemId(contentId, 'doc-')
    await updateDocument(documentId, {
      title: data.title,
      description: data.description,
      category: data.category,
      subcategory: data.subcategory,
      directory: data.directory,
      file: data.file,
    })
  }

  const handleContentURLUpdate = async (
    contentId: string,
    data: ContentManagerUrlUpdate,
  ) => {
    const urlId = parseContentItemId(contentId, 'url-')

    await updateURL(urlId, {
      title: data.title,
      description: data.description,
      url: data.url,
      category: data.category,
      subcategory: data.subcategory,
    })
  }

  const handleContentItemsDelete = async (contentIds: string[]) => {
    const documentIds: string[] = []
    const urlIds: string[] = []
    const directoryIds: string[] = []

    contentIds.forEach((contentId) => {
      if (contentId.startsWith('doc-')) {
        documentIds.push(parseContentItemId(contentId, 'doc-'))
      } else if (contentId.startsWith('url-')) {
        urlIds.push(parseContentItemId(contentId, 'url-'))
      } else if (contentId.startsWith('dir-')) {
        directoryIds.push(parseContentItemId(contentId, 'dir-'))
      }
    })

    const results = await Promise.allSettled([
      ...documentIds.map((documentId) => deleteDocument(documentId)),
      ...urlIds.map((urlId) => deleteURL(urlId)),
      ...directoryIds.map((directoryId) => deleteDirectory(directoryId)),
    ])

    const failed = results.find((result) => result.status === 'rejected') as PromiseRejectedResult | undefined
    if (failed) {
      throw failed.reason
    }
  }

  const handleContentFolderCreate = async (payload: { name: string; parentId: string | null }) => {
    await createDirectory({
      name: payload.name,
      parent: payload.parentId ? parseInt(parseContentItemId(payload.parentId, 'dir-'), 10) : null,
    })
  }

  const handleContentFolderUpdate = async (
    folderId: string,
    payload: { name?: string; parentId?: string | null },
  ) => {
    const directoryId = parseContentItemId(folderId, 'dir-')
    await updateDirectory(directoryId, {
      name: payload.name,
      parent: payload.parentId === undefined
        ? undefined
        : payload.parentId
          ? parseInt(parseContentItemId(payload.parentId, 'dir-'), 10)
          : null,
    })
  }

  const handleContentItemsMove = async (moves: ContentManagerMovePayload) => {
    const operations = moves.map(async ({ contentId, targetFolderId }) => {
      const directory = targetFolderId ? parseContentItemId(targetFolderId, 'dir-') : null
      if (contentId.startsWith('doc-')) {
        const documentId = parseContentItemId(contentId, 'doc-')
        await updateDocument(documentId, { directory })
        return
      }
      if (contentId.startsWith('url-')) {
        const urlId = parseContentItemId(contentId, 'url-')
        await updateURL(urlId, { directory })
        return
      }
      if (contentId.startsWith('dir-')) {
        const directoryId = parseContentItemId(contentId, 'dir-')
        await updateDirectory(directoryId, {
          parent: directory ? parseInt(directory, 10) : null,
        })
      }
    })

    await Promise.all(operations)
  }

  // Document and URL upload handlers - exact same logic as original frontend

  const handleURLUpload = async (data: URLUploadData) => {
    try {
      await uploadURL(data)
      setShowURLUpload(false)
    } catch (error) {
      console.error('URL upload failed:', error)
      // No cerrar el modal ni mostrar modal de error - dejar que el formulario maneje el error
      // El error se mostrará en el formulario de URL
      throw error // Re-lanzar el error para que el formulario lo capture
    }
  }

  // Lo que se acaba de subir, mientras su guidedUpload sigue abierta (4.6, D11). El
  // dialogo **no se cierra al subir**: un archivo nace excluido de la busqueda
  // y necesita un si, y ese si se pide donde esta la atencion, no en otra
  // pantalla a la que hay que acordarse de ir.
  const [recienSubido, setRecienSubido] = useState<{ id: string; titulo: string } | null>(null)

  const handleDocumentUpload = async (data: DocumentUploadData) => {
    try {
      const documento = await uploadDocument(data)
      setRecienSubido({ id: String(documento.id), titulo: documento.title })
    } catch (error) {
      console.error('Document upload failed:', error)
      
      // Mostrar modal de error con progreso
      const filename = data.file?.name || 'documento'
      const errorMessage = error instanceof Error ? error.message : String(error)
      
      setUploadErrorData({
        filename,
        stage: 'error',
        progress: 100,
        error: errorMessage,
        processingResult: {
          message: 'Error en la carga del documento',
          error_type: 'upload_failed'
        }
      })
      setShowUploadErrorModal(true)
      setShowDocumentUpload(false)
    }
  }

  const mappedCategories = useMemo(
    () =>
      managerCategories.map((cat) => ({
        id: cat.id,
        name: cat.name,
        subcategories: cat.subcategories.map((sub) => ({ id: sub.id, name: sub.name })),
      })),
    [managerCategories]
  )

  const contentItems = useMemo<ContentManagerItem[]>(() => {
    const folderItems: ContentManagerItem[] = []
    const knownFolderIds = new Set<string>()

    const walkDirectories = (nodes: DirectoryNode[], parentId: string | null) => {
      nodes.forEach((node) => {
        const folderId = `dir-${node.id}`
        knownFolderIds.add(folderId)
        folderItems.push({
          id: folderId,
          kind: "folder",
          name: node.name,
          parentId,
        })
        walkDirectories(node.children, folderId)
      })
    }

    walkDirectories(directories, null)

    const normalizeStatus = (status?: string): "uploaded" | "processing" | "embedded" | "failed" | undefined => {
      if (!status) return undefined
      if (status === "pending") return "processing"
      if (status === "uploaded" || status === "processing" || status === "embedded" || status === "failed") return status
      return undefined
    }

    const docItems: ContentManagerItem[] = documents.map((doc) => {
      const parentFolderId = doc.directory ? `dir-${doc.directory}` : null
      const parentId = parentFolderId && knownFolderIds.has(parentFolderId) ? parentFolderId : null
      return {
        id: `doc-${doc.id}`,
        kind: "document",
        name: doc.title || doc.name,
        parentId,
        description: doc.description,
        category: doc.category,
        subcategory: doc.subcategory,
        fileType: doc.type,
        size: doc.size,
        status: normalizeStatus(doc.status),
        addedDate: doc.addedDate,
        filePath: doc.downloadUrl || doc.file,
        tagIds: doc.tags?.map((tag) => tag.tag_id) ?? [],
      }
    })

    const urlItems: ContentManagerItem[] = urls.map((url) => {
      const parentFolderId = url.directory ? `dir-${url.directory}` : null
      const parentId = parentFolderId && knownFolderIds.has(parentFolderId) ? parentFolderId : null
      return {
        id: `url-${url.id}`,
        kind: "url",
        name: url.title,
        parentId,
        description: url.description,
        category: url.category,
        subcategory: url.subcategory,
        url: url.url,
        isPublic: url.isPublic,
        status: normalizeStatus(url.status),
        addedDate: url.addedDate,
      }
    })

    return [...folderItems, ...docItems, ...urlItems]
  }, [directories, documents, urls])

  const contentStats = useMemo(() => {
    const timestamps = [
      ...documents.map((doc) => doc.lastUpdatedAt ?? doc.addedDate),
      ...urls.map((url) => url.lastUpdatedAt ?? url.addedDate),
    ]
      .map((value) => {
        if (!value) return 0
        const parsed = new Date(value).getTime()
        return Number.isFinite(parsed) ? parsed : 0
      })
      .filter((value) => value > 0)

    const lastUpdated = timestamps.length > 0 ? new Date(Math.max(...timestamps)) : new Date()

    return {
      totalItems: contentItems.length,
      totalUrls: urls.length,
      totalDocuments: documents.length,
      lastUpdated,
    }
  }, [contentItems.length, documents, urls])

  return (
    <div className="flex h-full bg-background">
      <TooltipProvider delayDuration={0}>
        <div className="flex h-full w-full">
          {/* Main Content Area */}
          <div className="flex flex-1 flex-col">
            {/* Aqui habia un div vacio con un borde: la pagina no tenia titulo,
                solo una raya suelta arriba. */}
            <div className="px-3 pt-4 pb-3 md:px-6 md:pt-6">
              <PageHeader
                title={t('knowledge.page_title')}
                description={t('knowledge.page_description')}
              />
            </div>

            {/* Tabs outside header to enable proper height calc */}
            <div className="flex min-h-0 flex-1 flex-col">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col">
                <div className="border-b bg-background relative">
                  {/* Left scroll arrow with gradient */}
                  {canScrollLeft && (
                    <div className="absolute left-0 top-0 bottom-0 w-12 flex items-center pointer-events-none z-10">
                      {/* Gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent pointer-events-none" />
                      {/* Scroll arrow */}
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Desplazar pestanas"
                        className="relative z-10 pointer-events-auto"
                        onMouseDown={(e) => {
                          e.preventDefault()
                          const container = tabsContainerRef.current
                          if (container) {
                            const scrollInterval = setInterval(() => {
                              container.scrollLeft -= 50
                            }, 50)
                            
                            const stopScrolling = () => {
                              clearInterval(scrollInterval)
                              document.removeEventListener('mouseup', stopScrolling)
                              document.removeEventListener('mouseleave', stopScrolling)
                            }
                            
                            document.addEventListener('mouseup', stopScrolling)
                            document.addEventListener('mouseleave', stopScrolling)
                          }
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </Button>
                    </div>
                  )}
                  
                  {/* Right scroll arrow with gradient */}
                  {canScrollRight && (
                  <div className="absolute right-0 top-0 bottom-0 w-12 flex items-center pointer-events-none z-10">
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-l from-background via-background/80 to-transparent pointer-events-none" />
                    {/* Scroll arrow */}
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Desplazar pestanas"
                      className="relative z-10 pointer-events-auto"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        const container = tabsContainerRef.current
                        if (container) {
                          const scrollInterval = setInterval(() => {
                            container.scrollLeft += 50
                          }, 50)
                          
                          const stopScrolling = () => {
                            clearInterval(scrollInterval)
                            document.removeEventListener('mouseup', stopScrolling)
                            document.removeEventListener('mouseleave', stopScrolling)
                          }
                          
                          document.addEventListener('mouseup', stopScrolling)
                          document.addEventListener('mouseleave', stopScrolling)
                        }
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Button>
                  </div>
                  )}
                  
                  <TabsList 
                    ref={tabsContainerRef}
                    className="h-14 w-full justify-start rounded-none border-b-0 bg-transparent p-0 px-6 overflow-x-auto scrollbar-hide"
                    style={{
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none'
                    }}
                  >
                    {tabs.map((tab) => (
                      <TabsTrigger
                        key={tab.id}
                        value={tab.id}
                        className="flex items-center gap-2 rounded-none border-b-2 border-transparent px-6 py-4 text-sm font-medium data-[state=active]:border-primary data-[state=active]:bg-background data-[state=active]:text-primary flex-shrink-0"
                      >
                        {tab.icon}
                        <span className="hidden sm:inline">{tab.name}</span>
                      </TabsTrigger>
                    ))}
                  </TabsList>
              </div>
              
              {/* Content Area */}
                <div className="flex min-h-0 flex-1 overflow-hidden">
                  {/* Main Content (full width, sidebar removed) */}
                  <div className="relative min-w-0 flex-1 overflow-hidden">
                    
                    <TabsContent value="chat" className="m-0 h-full min-w-0 border-0 p-0">
                        <RagTester 
                          messages={ragMessages}
                          onMessagesChange={setRagMessages}
                          translations={{
                            knowledge: {
                              rag: {
                                title: t('knowledge.rag.title'),
                                badge: t('knowledge.rag.badge'),
                                description: t('knowledge.rag.description'),
                                input_placeholder: t('knowledge.rag.input_placeholder'),
                                assistant_instructions: t('knowledge.rag.assistant_instructions'),
                                welcome: t('knowledge.rag.welcome'),
                                error_generic: t('knowledge.rag.error_generic'),
                                clear_chat: t('knowledge.rag.clear_chat'),
                                thinking: t('knowledge.rag.thinking'),
                                empty_state_title: t('knowledge.rag.empty_state_title'),
                                empty_state_description: t('knowledge.rag.empty_state_description'),
                                suggestion_1: t('knowledge.rag.suggestion_1'),
                                suggestion_2: t('knowledge.rag.suggestion_2'),
                                suggestion_3: t('knowledge.rag.suggestion_3'),
                                metadata_title: t('knowledge.rag.metadata_title'),
                                tabs: {
                                  performance: t('knowledge.rag.tabs.performance'),
                                  context: t('knowledge.rag.tabs.context'),
                                  debug: t('knowledge.rag.tabs.debug')
                                },
                                performance: {
                                  processing_time: t('knowledge.rag.performance.processing_time'),
                                  confidence: t('knowledge.rag.performance.confidence')
                                },
                                context: {
                                  documents_used: t('knowledge.rag.context.documents_used'),
                                  sources: t('knowledge.rag.context.sources')
                                },
                                debug: {
                                  service: t('knowledge.rag.debug.service'),
                                  processing_thoughts: t('knowledge.rag.debug.processing_thoughts')
                                }
                              }
                            }
                          }}
                          categories={managerCategories}
                        />
                    </TabsContent>

                    <TabsContent value="content" className="m-0 h-full border-0 p-0">
                      <div className="h-full overflow-auto p-3 md:p-6">
                        <ContentManager
                          stats={contentStats}
                          items={contentItems}
                          categories={formCategories}
                          loading={documentsLoading}
                          onRequestDocumentUpload={askForFile}
                          onRequestURLUpload={() => setShowURLUpload(true)}
                          onCreateFolder={handleContentFolderCreate}
                          onUpdateFolder={handleContentFolderUpdate}
                          onMoveItems={handleContentItemsMove}
                          onUpdateDocument={handleContentDocumentUpdate}
                          onUpdateURL={handleContentURLUpdate}
                          onDeleteItems={handleContentItemsDelete}
                        />
                      </div>
                    </TabsContent>

                    <TabsContent value="documents" className="m-0 h-full border-0 p-0">
                      <div className="h-full overflow-auto p-3 md:p-6">
                        <Documents 
                          onUploadClick={askForFile}
                          onFormClick={() => setShowDocumentUpload(true)} 
                          documents={documents}
                        loading={documentsLoading}
                        error={documentsError}
                          onDelete={deleteDocument}
                          onUpdate={updateDocument}
                          onTagsUpdate={refetchDocuments}
                        categories={mappedCategories}
                        translations={{ 
                          knowledge: { 
                              filters: { documents: t('knowledge.sections.documents') },
                              documents_description: t('knowledge.documents_description'),
                              upload_document: t('knowledge.upload_document'),
                              upload_form: t('knowledge.upload_form'),
                            delete_confirmation: {
                              title: t('knowledge.delete_confirmation.title'),
                                document_message: t('knowledge.delete_confirmation.document_message'),
                              description: t('knowledge.delete_confirmation.description'),
                              deleting: t('knowledge.delete_confirmation.deleting')
                            }
                          },
                          functions: {
                            cancel: t('functions.cancel'),
                            delete: t('functions.delete')
                          }
                        }}
                      />
                      </div>
                    </TabsContent>

                    <TabsContent value="urls" className="m-0 h-full border-0 p-0">
                      <div className="h-full overflow-auto p-3 md:p-6">
                        <URLs 
                          onAddClick={() => setShowURLUpload(true)} 
                          urls={urls}
                        loading={documentsLoading}
                        error={documentsError}
                          onDelete={deleteURL}
                          onUpdate={updateURL}
                        categories={mappedCategories}
                        translations={{ 
                          knowledge: { 
                              filters: { urls: t('knowledge.sections.urls') },
                              urls_description: t('knowledge.urls_description'),
                              add_url: t('knowledge.add_url'),
                            delete_confirmation: {
                              title: t('knowledge.delete_confirmation.title'),
                                url_message: t('knowledge.delete_confirmation.url_message'),
                              description: t('knowledge.delete_confirmation.description'),
                              deleting: t('knowledge.delete_confirmation.deleting')
                            }
                          },
                          functions: {
                            cancel: t('functions.cancel'),
                            delete: t('functions.delete')
                          }
                        }}
                      />
                    </div>
                    </TabsContent>
                  
                    <TabsContent value="categories" className="m-0 h-full border-0 p-0">
                      <ScrollArea className="h-full">
                        <div className="flex flex-col gap-4 p-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>{t('knowledge.categories.title')}</CardTitle>
                        <CardDescription>{t('knowledge.categories.description')}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <CategoriesPanel
                          loading={categoriesLoading}
                          error={categoriesError}
                          categories={managerCategories}
                          expandedIds={expandedIds}
                          isAddingCategory={isAddingCategory}
                          newCategoryName={newCategoryName}
                          onToggle={toggleCategory}
                          onEdit={handleEditCategory}
                          onDelete={handleDeleteCategory}
                          onAddSubcategory={handleShowAddSubcategoryDialog}
                          onStartAddingCategory={() => setIsAddingCategory(true)}
                          onCancelAddingCategory={() => {
                            setIsAddingCategory(false)
                            setNewCategoryName("")
                          }}
                          onNewCategoryNameChange={setNewCategoryName}
                          onAddCategory={handleAddCategory}
                          retryLabel={t('knowledge.categories.try_again')}
                          addCategoryLabel={t('knowledge.categories.add_category')}
                          categoryNamePlaceholder={t('knowledge.categories.category_name')}
                          saveLabel={t('knowledge.categories.save')}
                          cancelLabel={t('knowledge.categories.cancel')}
                        />
                      </CardContent>
                    </Card>
                        </div>
                      </ScrollArea>
                    </TabsContent>

                    {/* Lo que sabe tu agente (4.1, D7). La seccion Workspace
                        se quito el 2026-09-17 por pedirle al cliente
                        decisiones cuyo efecto no veia; esto va en Knowledge,
                        que es donde ya viene a alimentar al agente. */}
                    <TabsContent value="lo-que-sabe" className="m-0 h-full border-0 p-0">
                      <WhatTheAgentKnows />
                    </TabsContent>

                    <TabsContent value="tags" className="m-0 h-full border-0 p-0 overflow-hidden">
                      <TagsManager />
                    </TabsContent>



                </div>
                </div>
              </Tabs>
            </div>
          </div>
          
          {/* Mobile sidebar removed — stats now shown as hover badge in Content tab */}
        </div>

        {/* Document Upload Modal */}
        {showDocumentUpload && (
          <Dialog
            open={showDocumentUpload}
            onOpenChange={(abierto: boolean) => {
              if (!abierto) setRecienSubido(null)
              setShowDocumentUpload(abierto)
            }}
          >
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {recienSubido ? t('knowledge.subida.titulo') : t('knowledge.upload_document')}
                </DialogTitle>
                <DialogDescription>
                  {recienSubido
                    ? t('knowledge.subida.esperan')
                    : t('knowledge.upload_document_description')}
                </DialogDescription>
              </DialogHeader>
              {recienSubido ? (
                <UploadGate
                  documento={recienSubido.id}
                  archivo={recienSubido.titulo}
                  onCerrar={() => {
                    setRecienSubido(null)
                    setShowDocumentUpload(false)
                  }}
                />
              ) : (
                <DocumentUploadForm
                  onUpload={handleDocumentUpload}
                  onCancel={() => {
                    setShowDocumentUpload(false)
                  }}
                  categories={formCategories}
                  loading={documentsUploadLoading}
                />
              )}
            </DialogContent>
          </Dialog>
        )}

        {/* URL Upload Modal */}
        {showURLUpload && (
          <Dialog open={showURLUpload} onOpenChange={setShowURLUpload}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Subir URL</DialogTitle>
                <DialogDescription>
                  Añade una URL para extraer su contenido y añadirlo a la base de conocimiento
                </DialogDescription>
              </DialogHeader>
              <URLUploadForm
                onUpload={handleURLUpload}
                onCancel={() => {
                  setShowURLUpload(false)
                }}
                categories={formCategories}
                loading={documentsUploadLoading}
                existingUrls={existingUrls}
              />
            </DialogContent>
          </Dialog>
        )}

      {/* Diálogo para añadir subcategoría */}
      <Dialog open={showSubcategoryDialog} onOpenChange={(open: boolean) => !open && setShowSubcategoryDialog(false)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Añadir Subcategoría</DialogTitle>
            <DialogDescription>
                Añadir subcategoría a "{currentParentId ? findCategoryName(currentParentId) : "categoría"}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={newSubcategoryName}
              onChange={(e) => setNewSubcategoryName(e.target.value)}
              placeholder="Nombre de la subcategoría"
              className="w-full"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddSubcategory()
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubcategoryDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddSubcategory}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de confirmación para eliminar categoría */}
      <Dialog open={showDeleteDialog !== null} onOpenChange={(open: boolean) => !open && setShowDeleteDialog(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Confirmar eliminación</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que quieres eliminar esta categoría? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={() => showDeleteDialog && confirmDeleteCategory(showDeleteDialog)}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Error de Carga */}
      {uploadErrorData && (
        <UploadErrorModal
          open={showUploadErrorModal}
          onClose={() => {
            setShowUploadErrorModal(false)
            setUploadErrorData(null)
          }}
          filename={uploadErrorData.filename}
          stage={uploadErrorData.stage}
          progress={uploadErrorData.progress}
          error={uploadErrorData.error}
          processingResult={uploadErrorData.processingResult}
        />
      )}
      </TooltipProvider>
    </div>
  )
}
